import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Compass, Crosshair, Loader2, X } from "lucide-react";
import { fmtKm } from "./regiaoMapaHelpers";

// Região do bairro (Iter 108): mostra a atual × sugerida pela posição; quem tem bairros.a troca direto (PUT /bairros/{id}).
function RegiaoDoBairro({ bairro, regioes, canEdit, onSaved }) {
  const [saving, setSaving] = useState(false);
  const s = bairro.sugestao;
  const trocar = async (v) => {
    setSaving(true);
    try {
      await api.put(`/bairros/${bairro.id}`, { regiao_id: v });
      toast.success(`"${bairro.nome}" → ${regioes.find((r) => r.id === v)?.nome || "região"}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao trocar região"); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded border bg-slate-50/60 p-2 space-y-1" data-testid="mapa-bairro-regiao">
      <div className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-1"><Compass className="w-3 h-3" />Região</div>
      {canEdit && regioes.length > 0 ? (
        <Select value={bairro.regiao_id || ""} onValueChange={trocar} disabled={saving}>
          <SelectTrigger className={`h-8 text-xs bg-white ${!bairro.regiao_id ? "border-rose-300" : ""}`} data-testid="mapa-bairro-regiao-select"><SelectValue placeholder="Sem região — escolha" /></SelectTrigger>
          <SelectContent>{regioes.map((r) => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <div className="text-sm" data-testid="mapa-bairro-regiao-nome">{bairro.regiao_nome || <span className="text-muted-foreground">sem região</span>}</div>
      )}
      {s && (
        <div className={`text-[11px] ${bairro.fora_da_zona ? "text-amber-800" : "text-muted-foreground"}`} data-testid="mapa-bairro-sugestao" data-fora={bairro.fora_da_zona ? "1" : "0"}>
          {bairro.fora_da_zona && <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-600" />}
          Sugerida pela posição: <b>{s.regiao_nome || s.zona_label}</b> · {fmtKm(s.dist_km)} do centro
          {canEdit && s.regiao_id && s.regiao_id !== bairro.regiao_id && (
            <button type="button" className="ml-2 underline text-sky-700 hover:text-sky-900" onClick={() => trocar(s.regiao_id)} disabled={saving} data-testid="mapa-bairro-aplicar-sugestao">aplicar</button>
          )}
        </div>
      )}
    </div>
  );
}

// Painel lateral do bairro selecionado no mapa: escolher em quais rotas ele está.
export function BairroPopupCard({ bairro, rotas, regioes = [], canEdit, canEditRegiao = false, onClose, onSaved, onPlace }) {
  const [sel, setSel] = useState(new Set());
  const [saving, setSaving] = useState(false);
  useEffect(() => { setSel(new Set((bairro.rotas || []).map((r) => r.id))); }, [bairro]);

  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/bairros/${bairro.id}/rotas`, { rota_ids: [...sel] });
      toast.success(`Rotas de "${bairro.nome}" atualizadas`);
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-orange-200" data-testid="mapa-bairro-card">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-semibold" data-testid="mapa-bairro-nome">{bairro.nome}</div>
            <div className="text-[11px] text-muted-foreground">{bairro.entregas_hoje || 0} entrega(s) hoje{bairro.source === "manual" ? " · posição manual" : bairro.polygon ? " · polígono OSM" : ""}</div>
          </div>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose} data-testid="mapa-bairro-close"><X className="w-4 h-4" /></Button>
        </div>
        <RegiaoDoBairro bairro={bairro} regioes={regioes} canEdit={canEditRegiao} onSaved={onSaved} />
        <div className="text-xs font-bold uppercase text-muted-foreground">Rotas deste bairro</div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {rotas.map((r) => (
            <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer rounded px-1 py-0.5 hover:bg-slate-50">
              <input type="checkbox" checked={sel.has(r.id)} disabled={!canEdit} onChange={() => toggle(r.id)} className="accent-orange-600 w-4 h-4" data-testid={`mapa-bairro-rota-${r.id}`} />
              <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: r.cor }} />
              <span className="flex-1 truncate">{r.nome}</span>
              {r.ativa === false && <span className="text-[10px] text-slate-500">inativa</span>}
            </label>
          ))}
          {rotas.length === 0 && <div className="text-xs text-muted-foreground">Cadastre rotas na aba "Cadastro".</div>}
        </div>
        {sel.size > 1 && <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-1.5">Em {sel.size} rotas: a entrega pedirá escolha manual da rota.</div>}
        {canEdit && (
          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onPlace} data-testid="mapa-bairro-place"><Crosshair className="w-3 h-3 mr-1" />Reposicionar</Button>
            <Button size="sm" className="h-8 flex-1 bg-orange-600 hover:bg-orange-700" onClick={save} disabled={saving} data-testid="mapa-bairro-save">
              {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}Salvar
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
